/**
 * The complete transition table of R9.12.
 *
 * R9.13 states: "A reason (10-500 characters, trimmed) SHALL be required for
 * every status transition other than Draft → Submitted."
 *
 * R9.3 states: "WHERE the requested transition target is Approved, or the
 * requested transition moves the Initiative out of status Approved or Final, THE
 * Authorization_Service SHALL permit the transition only for a user recorded in
 * role administration as holding approver rights."
 *
 * Approver rights are **a per-user flag recorded in role administration**, stored
 * on `complan.app_user.approver_rights` and surfaced as `Principal.approverRights`.
 * They are deliberately *not* derived from role membership: assumption A2 leaves
 * open "who holds Approver_Rights relative to the Commercial Planning Team role",
 * so tying the two together would decide that question. Holding the flag and
 * holding a role that can write an Initiative are separate conditions, and a
 * transition needs both - the flag here, and `initiative:write` at the route.
 *
 * (An earlier version of this comment claimed approver rights meant
 * `has_role('commercial_planning_manager')`. There is no such role: the seven are
 * in `COMPLAN_ROLES`, and no role-membership test has ever governed this flag.)
 *
 * Transition table from R9.12:
 * - Draft → Submitted (reason optional per R9.13)
 * - Draft → Cancelled (reason required per R9.13)
 * - Submitted → Planned (approver rights not required, reason required per R9.13)
 * - Submitted → Draft (reason required per R9.13)
 * - Submitted → Cancelled (reason required per R9.13)
 * - Planned → Approved (approver rights required, reason required per R9.13)
 * - Planned → Submitted (reason required per R9.13)
 * - Planned → Cancelled (reason required per R9.13)
 * - Approved → Final (approver rights required, reason required per R9.13)
 * - Approved → Planned (approver rights required, reason required per R9.13)
 * - Approved → Cancelled (reason required per R9.13)
 * - Final → Cancelled (approver rights required per R9.13, reason required per R9.13)
 * - Cancelled → Draft (reason required per R9.13)
 */
const TRANSITIONS = Object.freeze([
    // From Draft
    { from: 'draft', to: 'submitted', requiresApproverRights: false, requiresReason: false },
    { from: 'draft', to: 'cancelled', requiresApproverRights: false, requiresReason: true },
    // From Submitted
    { from: 'submitted', to: 'planned', requiresApproverRights: false, requiresReason: true },
    { from: 'submitted', to: 'draft', requiresApproverRights: false, requiresReason: true },
    { from: 'submitted', to: 'cancelled', requiresApproverRights: false, requiresReason: true },
    // From Planned
    { from: 'planned', to: 'approved', requiresApproverRights: true, requiresReason: true },
    { from: 'planned', to: 'submitted', requiresApproverRights: false, requiresReason: true },
    { from: 'planned', to: 'cancelled', requiresApproverRights: false, requiresReason: true },
    // From Approved
    { from: 'approved', to: 'final', requiresApproverRights: true, requiresReason: true },
    { from: 'approved', to: 'planned', requiresApproverRights: true, requiresReason: true },
    { from: 'approved', to: 'cancelled', requiresApproverRights: false, requiresReason: true },
    // From Final
    { from: 'final', to: 'approved', requiresApproverRights: true, requiresReason: true },
    { from: 'final', to: 'cancelled', requiresApproverRights: true, requiresReason: true },
    // From Cancelled
    { from: 'cancelled', to: 'draft', requiresApproverRights: false, requiresReason: true },
]);
/**
 * Attempt a transition from one status to another.
 *
 * Returns either a permitted transition (with the reason requirement) or a
 * rejection carrying the current status and the complete list of permitted
 * target statuses from that status, subject to the principal's approver rights.
 *
 * @param from - The current status
 * @param to - The requested target status
 * @param hasApproverRights - Whether the requesting user holds approver rights
 *   (i.e., has_role('commercial_planning_manager') per R9.3)
 * @returns The transition result: permitted or denied with permitted targets
 */
export function attemptTransition(from, to, hasApproverRights) {
    // Find the transition in the table
    const transition = TRANSITIONS.find((t) => t.from === from && t.to === to);
    // If the transition doesn't exist in the table, it's not permitted
    if (!transition) {
        return {
            permitted: false,
            currentStatus: from,
            permittedTargets: permittedTargetsFrom(from, hasApproverRights),
        };
    }
    // If the transition requires approver rights and the user doesn't have them,
    // it's not permitted
    if (transition.requiresApproverRights && !hasApproverRights) {
        return {
            permitted: false,
            currentStatus: from,
            permittedTargets: permittedTargetsFrom(from, hasApproverRights),
        };
    }
    // The transition is permitted
    return {
        permitted: true,
        requiresReason: transition.requiresReason,
    };
}
/**
 * Get the complete list of permitted target statuses from a given status,
 * subject to the principal's approver rights.
 *
 * @param from - The current status
 * @param hasApproverRights - Whether the requesting user holds approver rights
 * @returns The list of permitted target statuses
 */
function permittedTargetsFrom(from, hasApproverRights) {
    const targets = TRANSITIONS.filter((t) => t.from === from && (!t.requiresApproverRights || hasApproverRights)).map((t) => t.to);
    return Object.freeze(targets);
}
//# sourceMappingURL=lifecycle.js.map